import { createFileRoute } from "@tanstack/react-router";

import { ServicePageLayout } from "../components/site/ServicePageLayout";
import { ContactForm } from "../components/home/ContactForm";
import { ClientsMarquee } from "../components/shared/ClientsMarquee";
import { Portfolio } from "../components/shared/Portfolio";
import { Reviews } from "../components/shared/Reviews";
import { Faq } from "../components/shared/Faq";
import type { FaqItem } from "../components/shared/Faq";
import { WebServicesHero } from "../components/web-services/WebServicesHero";
import { WebServicesProducts } from "../components/web-services/WebServicesProducts";
import { WebServicesMiniApps } from "../components/web-services/WebServicesMiniApps";
import { WebServicesWhen } from "../components/web-services/WebServicesWhen";
import { WebServicesCompare } from "../components/web-services/WebServicesCompare";
import { WebServicesMvp } from "../components/web-services/WebServicesMvp";
import { WebServicesStack } from "../components/web-services/WebServicesStack";
import { WebServicesReliability } from "../components/web-services/WebServicesReliability";
import { WebServicesFlow } from "../components/web-services/WebServicesFlow";
import { WebServicesPricing } from "../components/web-services/WebServicesPricing";
import { WebServicesLinks } from "../components/web-services/WebServicesLinks";
import { esgCase, systemLogisticCase, archidicaCase } from "../content/casesAll";
import { SITE_URL, breadcrumbJsonLd, buildMeta, serviceJsonLd } from "../lib/site";
import { crumbTuples } from "../lib/breadcrumbs";

const title = "Разработка веб-сервисов и Mini Apps под ключ | HAY.agency";
const description =
  "Разрабатываем личные кабинеты, B2B- и B2C-порталы, SaaS, внутренние системы и Telegram Mini Apps: аналитика, UX, backend, frontend и интеграции.";

const faqItems: FaqItem[] = [
  {
    q: "Чем веб-сервис отличается от сайта?",
    a: "Сайт представляет компанию и приводит заявки, а веб-сервис обслуживает пользователей и процессы: авторизация, роли, данные, расчёты, отчётность и автоматизация операций.",
  },
  {
    q: "Сколько стоит разработка веб-сервиса?",
    a: "Стоимость определяется после предпроектной аналитики. Она зависит от количества ролей и сценариев, сложности бизнес-логики, объёма интеграций и требований к отчётности. После аналитики предоставляем состав работ и смету.",
  },
  {
    q: "Сколько времени занимает разработка?",
    a: "Срок зависит от объёма первого релиза. Мы стартуем с MVP: собираем ключевой сценарий, запускаем его на реальных пользователях и дальше развиваем продукт итерациями по приоритетам.",
  },
  {
    q: "Что такое Telegram Mini App и кому он подходит?",
    a: "Mini App — цифровой сервис внутри Telegram: пользователь открывает его из бота и авторизуется своим аккаунтом. Формат подходит для личных кабинетов, каталогов, программ лояльности и сервисов с частым повторным использованием.",
  },
  {
    q: "Можно ли интегрировать сервис с CRM и 1С?",
    a: "Да. Подключаем amoCRM, Битрикс24, RetailCRM, 1С, платёжные сервисы, мессенджеры и аналитику. Состав интеграций фиксируем на этапе архитектуры.",
  },
  {
    q: "Как вы работаете: проект или почасовая модель?",
    a: "Используем обе модели. Проектная подходит, когда объём и состав функций зафиксированы. Time & Material — когда приоритеты уточняются от спринта к спринту.",
  },
  {
    q: "Что происходит после запуска?",
    a: "Развиваем продукт: добавляем функциональность релизами, следим за логами и стабильностью, обновляем интеграции и дорабатываем сценарии по обратной связи пользователей.",
  },
];

export const Route = createFileRoute("/web-services")({
  head: () => {
    const base = buildMeta({
      title,
      description,
      path: "/web-services",
      image: `${SITE_URL}/og/services/web-services.webp`,
      imageWidth: 1200,
      imageHeight: 630,
      imageAlt: "Разработка веб-сервисов и Mini Apps — HAY.agency",
      locale: "ru_RU",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": `${SITE_URL}/web-services#webpage`,
                url: `${SITE_URL}/web-services`,
                name: title,
                description,
                inLanguage: "ru-RU",
                isPartOf: { "@id": `${SITE_URL}/#website` },
                breadcrumb: { "@id": `${SITE_URL}/web-services#breadcrumbs` },
                mainEntity: { "@id": `${SITE_URL}/web-services#service` },
              },
              serviceJsonLd({
                name: "Разработка веб-сервисов, цифровых платформ и Mini Apps",
                description,
                path: "/web-services",
                id: `${SITE_URL}/web-services#service`,
                serviceType: "Разработка веб-сервисов и программных продуктов",
                providerId: `${SITE_URL}/#organization`,
                inGraph: true,
              }),
              breadcrumbJsonLd(crumbTuples("/web-services"), {
                id: `${SITE_URL}/web-services#breadcrumbs`,
                inGraph: true,
              }),
              {
                "@type": "FAQPage",
                "@id": `${SITE_URL}/web-services#faq`,
                mainEntity: faqItems.map((item) => ({
                  "@type": "Question",
                  name: item.q,
                  acceptedAnswer: { "@type": "Answer", text: item.a },
                })),
              },
            ],
          }),
        },
      ],
    };
  },
  component: WebServicesPage,
});

function WebServicesPage() {
  return (
    <ServicePageLayout breadcrumb="Веб-сервисы" jsonLd={false}>
      <WebServicesHero />
      <div className="pb-10 lg:pb-[74px]">
        <ClientsMarquee bare />
      </div>
      <WebServicesProducts />
      <WebServicesMiniApps />
      <WebServicesWhen />
      <WebServicesCompare />
      <WebServicesMvp />
      <WebServicesStack />
      <WebServicesReliability />
      <WebServicesFlow />
      <Portfolio
        title="Проекты со сложной логикой"
        items={[systemLogisticCase, esgCase, archidicaCase]}
        lead="Проекты, где мы проектировали структуру данных, интерфейсы и интеграции, а не только страницы сайта."
      />
      <WebServicesPricing />
      <Faq
        compact
        title="Частые вопросы о разработке веб-сервисов"
        lead="Отличия от сайта, стоимость, сроки, Mini Apps, интеграции и модели работы."
        items={faqItems}
      />
      <WebServicesLinks />
      <Reviews
        only={["Наала", "Рафаэл Манвелян", "Артем Авакимов", "Юлия"]}
        title="Отзывы клиентов"
      />
      <ContactForm
        formName="web_services"
        extra={{ product: "web_services" }}
      />
    </ServicePageLayout>
  );
}
